import { NavigationOptions, PaginationOptions, SwiperOptions } from 'swiper/types';
import Swiper from 'swiper';
import { Navigation, Pagination, Thumbs } from 'swiper/modules';
import initParams from 'store/initParams';

type SwiperMode = 'PICTURES' | 'CARDS' | 'THUMBS' | 'SUGGESTIONS';

const getNavigationOptions = (nextButton: HTMLElement, prevButton: HTMLElement): NavigationOptions => {
  return {
    nextEl: nextButton,
    prevEl: prevButton,
    enabled: !initParams.isMobile,
  };
};

const getPaginationOptions = (elPagination: HTMLElement): PaginationOptions => {
  return {
    el: elPagination,
    enabled: true,
    clickable: true,
    bulletActiveClass: '-active',
    bulletClass: 'nav-dot',
    bulletElement: 'li',
    dynamicBullets: true,
    type: 'bullets',
  };
};

const getCarouselOptions = (elContainer: HTMLElement, mode: SwiperMode, forceOptions?: SwiperOptions): SwiperOptions => {
  addHtmlElements(elContainer, mode);
  const prevButton = elContainer.querySelector('.swiper-button-prev-custom') as HTMLElement;
  const nextButton = elContainer.querySelector('.swiper-button-next-custom') as HTMLElement;
  const pagination = elContainer.querySelector('.swiper-pagination-custom') as HTMLElement;

  if (prevButton === null || nextButton === null || pagination === null) {
    throw new Error('Carousel elements not found');
  }

  const isPictureMode = mode === 'PICTURES';
  const isThumbMode = mode === 'THUMBS';
  const isSuggestionMode = mode === 'SUGGESTIONS';

  return {
    navigation: isThumbMode ? false : getNavigationOptions(nextButton, prevButton),
    pagination: isThumbMode || isSuggestionMode ? false : getPaginationOptions(pagination),
    slidesPerView: 'auto',
    roundLengths: isThumbMode,
    spaceBetween: isPictureMode || isSuggestionMode ? 0 : 15,
    watchSlidesProgress: isThumbMode,
    centerInsufficientSlides: isThumbMode,
    slidesOffsetBefore: isSuggestionMode ? 8 : 0,
    slidesOffsetAfter: isSuggestionMode ? 8 : 0,
    noSwiping: true,
    longSwipes: true,
    longSwipesMs: 100,
    longSwipesRatio: 0.2,
    speed: 100,
    resistance: false,
    simulateTouch: initParams.isMobile || isThumbMode,
    freeMode: isThumbMode,
    keyboard: false,
    mousewheel: true,
    grabCursor: !initParams.isMobile,
    ...(forceOptions ?? {}),
  };
};

const addHtmlElements = (elContainer: HTMLElement, mode: SwiperMode) => {
  const isSuggestionMode = mode === 'SUGGESTIONS';
  const buttonSize = isSuggestionMode ? '-sm' : '-md';

  const leftNav = `<div role="button" class="swiper-button-prev-custom eva-3-nav-slider ${buttonSize} -eva-3-shadow-line-hover -negative -left">
                    <i class="eva-3-icon-chevron-left nav-slider-icon"></i>
                  </div>`;
  const rightNav = `<div role="button" class="swiper-button-next-custom eva-3-nav-slider ${buttonSize} -eva-3-shadow-line-hover -negative -right">
                    <i class="eva-3-icon-chevron-right nav-slider-icon"></i>
                  </div>`;

  const pagination = `<div class="swiper-pagination-custom eva-3-nav-dots -carousel -lg"></div>`;

  elContainer.insertAdjacentHTML('beforeend', leftNav);
  elContainer.insertAdjacentHTML('beforeend', rightNav);
  elContainer.classList.add(`${mode.toLowerCase()}-carousel`);
  elContainer.children[0].insertAdjacentHTML('beforeend', pagination);
};

const removeHtmlElements = (elContainer: HTMLElement, navigation: boolean, pagination: boolean) => {
  if (!navigation) {
    elContainer.querySelector('.swiper-button-prev-custom')?.remove();
    elContainer.querySelector('.swiper-button-next-custom')?.remove();
  }

  if (!pagination) {
    elContainer.querySelector('.swiper-pagination-custom')?.remove();
  }
};

const toggleNavs = (swiper: Swiper) => {
  if (swiper.isEnd) {
    swiper.el.classList.add('no-next');
  } else {
    swiper.el.classList.remove('no-next');
  }

  if (swiper.isBeginning) {
    swiper.el.classList.add('no-prev');
  } else {
    swiper.el.classList.remove('no-prev');
  }
};

const updateCenteredSlides = (swiper: Swiper, mode: SwiperMode) => {
  const shouldCenter = initParams.isMobile && mode === 'CARDS';
  const notEdges = !swiper.isBeginning && !swiper.isEnd;

  if (shouldCenter) {
    swiper.params.centeredSlides = notEdges;
    swiper.params.slidesOffsetBefore = swiper.isBeginning ? 16 : 0;
    swiper.params.slidesOffsetAfter = swiper.isEnd ? 16 : 0;
    swiper.update();
  }
};

/**
 * Para usar el carousel es necesario la siguiente estructura html:
 * <div class="swiper-container">
 *   <div class="swiper">
 *     <div class="swiper-wrapper">
 *       ....
 *      </div>
 *   </div>
 *</div>
 *
 * La navegacion y las flechas se agregan automaticamente en runtime.
 * El parametro elContainer debe referenciar al elemento con las clase 'swiper-container' que contiene el carousel
 * @param elContainer
 * @param mode
 * @param forceOptions
 */

const initCarousel = (elContainer: HTMLElement | null, mode: SwiperMode, forceOptions?: SwiperOptions): Swiper => {
  if (elContainer === null) {
    throw new Error(`Carousel container is null`);
  }

  const carouselOptions = getCarouselOptions(elContainer, mode, forceOptions);

  removeHtmlElements(
    elContainer,
    typeof carouselOptions.navigation !== 'undefined' && carouselOptions.navigation !== false,
    typeof carouselOptions.pagination !== 'undefined' && carouselOptions.pagination !== false,
  );

  const swiper = new Swiper(elContainer.querySelector('.swiper') as HTMLElement, {
    modules: [Navigation, Pagination, Thumbs],
    ...carouselOptions,
    on: {
      afterInit: () => {
        elContainer.setAttribute('initialized', 'true');
      },
      fromEdge: toggleNavs,
      toEdge: toggleNavs,
      beforeInit: toggleNavs,
      init: swiper => updateCenteredSlides(swiper, mode),
      slideChange: swiper => updateCenteredSlides(swiper, mode),
    },
  });

  return swiper;
};

/**
 * Inicializa dos carousels, uno para las imagenes y otro para las thumbs y los sincroniza
 * @param elContainer
 * @param elContainerThumbs
 */
export const initCarouselWithThumbs = (elContainer: HTMLElement | null, elContainerThumbs: HTMLElement | null): [Swiper, Swiper] => {
  const thumbs = initCarousel(elContainerThumbs, 'THUMBS');

  const carousel = initCarousel(elContainer, 'PICTURES', {
    thumbs: {
      swiper: thumbs,
    },
  });

  return [thumbs, carousel];
};

/**
 * Inicializa todos los carousels dentro de un contenedor
 * @param container
 * @param mode
 * @param forceOptions
 */
export const initAllCarousels = (container: HTMLElement | null, mode: SwiperMode, forceOptions?: SwiperOptions) => {
  container?.querySelectorAll('.swiper-container').forEach(swiperContainer => {
    if (swiperContainer.getAttribute('initialized') !== 'true') {
      initCarousel(swiperContainer as HTMLElement, mode, forceOptions);
    }
  });
};
